import { useState, useCallback, useRef } from 'react';

import { useMutation } from '@zenbusiness/application-commons-graphql';
import { useCaptureException } from '@zenbusiness/application-commons-logging';

import { applyRenderStrategy } from '../components/utils/applyRenderStrategy';
import { CREATE_THREAD_MUTATION } from '../mutations/createThreadMutation';
import { SUBMIT_USER_INPUT_MUTATION } from '../mutations/submitUserInputMutation';
import { buildThreadIdentity } from '../utils/buildThreadIdentity';

/**
 * Hook for inline conversation management (federated components)
 *
 * Combines thread creation and message sending without navigation.
 * Perfect for self-contained conversational components like VeloQA.
 *
 * @returns {object} Hook state and methods
 */
export function useInlineConversation() {
  const captureException = useCaptureException({
    tags: {
      frontend: 'assistant-ui',
      operationName: 'inlineConversation'
    }
  });

  // State
  const [threadUuid, setThreadUuid] = useState(null);
  const [instructions, setInstructions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Refs
  const isCreatingThread = useRef(false);

  // Mutations
  const [createThread] = useMutation(CREATE_THREAD_MUTATION);
  const [submitUserInput] = useMutation(SUBMIT_USER_INPUT_MUTATION);

  /**
   * Process and add new instructions
   */
  const processInstructions = useCallback((currentInstructions, newInstructionData) => {
    let updatedInstructions = [...currentInstructions];

    newInstructionData.forEach((instruction) => {
      updatedInstructions = applyRenderStrategy(updatedInstructions, instruction);
    });

    return updatedInstructions;
  }, []);

  /**
   * Create a new thread
   */
  const createNewThread = useCallback(
    async (message, messageType, mode, { visitUuid, userUuid, businessEntityUuid }) => {
      if (isCreatingThread.current) {
        return null;
      }

      isCreatingThread.current = true;
      setError(null);

      try {
        const { identity, threadMode } = buildThreadIdentity(
          mode,
          visitUuid,
          userUuid,
          businessEntityUuid
        );

        const input = {
          businessEntityUuid,
          messageType,
          message,
          role: 'user'
        };

        const result = await createThread({
          variables: {
            identity,
            input,
            threadMode
          }
        });

        const threadData = result?.data?.createThread;
        const newThreadUuid = threadData?.threadUuid;
        const instructionData = threadData?.data || [];

        if (threadData?.error) {
          const responseError = new Error(threadData.error.message);
          responseError.code = threadData.error.code;
          setError(responseError);
          throw responseError;
        }

        if (!newThreadUuid) {
          const err = new Error('Thread creation failed: No thread UUID returned');
          setError(err);
          throw err;
        }

        // Store thread UUID
        setThreadUuid(newThreadUuid);

        // Process and store instructions
        const newInstructions = processInstructions([], instructionData);
        setInstructions(newInstructions);

        return { threadUuid: newThreadUuid, instructions: newInstructions };
      } catch (err) {
        captureException(err, {
          tags: {
            errorType: 'thread_creation_failed',
            userMode: mode
          }
        });
        setError(err);
        throw err;
      } finally {
        isCreatingThread.current = false;
      }
    },
    [createThread, processInstructions, captureException]
  );

  /**
   * Send message to existing thread
   */
  const sendMessage = useCallback(
    async (message, messageType, mode, { visitUuid, userUuid, businessEntityUuid }) => {
      if (!threadUuid) {
        throw new Error('No thread UUID available. Thread must be created first.');
      }

      setError(null);

      try {
        const { identity } = buildThreadIdentity(mode, visitUuid, userUuid, businessEntityUuid);

        const inputObject = {
          businessEntityUuid,
          messageType,
          message,
          role: 'user'
        };

        // Get last message UUID for threading
        const lastMessageUuid =
          instructions.length > 0 ? instructions[instructions.length - 1].id : null;

        const response = await submitUserInput({
          variables: {
            identity,
            input: inputObject,
            threadUuid,
            messageUuid: lastMessageUuid,
            mode: 'post'
          }
        });

        const responseData = response?.data?.interactWithAssistant;
        const newInstructionData = responseData?.data || [];

        if (responseData && (!responseData.ok || responseData.error)) {
          const serverError = new Error(responseData.error?.message || 'Server error in response');
          setError(serverError);
          throw serverError;
        }

        // Process and update instructions
        const updatedInstructions = processInstructions(instructions, newInstructionData);
        setInstructions(updatedInstructions);

        return { instructions: updatedInstructions };
      } catch (err) {
        captureException(err, {
          tags: {
            errorType: 'message_send_failed',
            threadUuid,
            userMode: mode
          }
        });
        setError(err);
        throw err;
      }
    },
    [threadUuid, instructions, submitUserInput, processInstructions, captureException]
  );

  /**
   * Send a message (creates thread if needed)
   */
  const sendMessageOrCreateThread = useCallback(
    async (message, messageType, mode, identityParams) => {
      setLoading(true);

      try {
        if (threadUuid) {
          // Thread exists, send message
          return await sendMessage(message, messageType, mode, identityParams);
        }
        // No thread, create one
        return await createNewThread(message, messageType, mode, identityParams);
      } finally {
        setLoading(false);
      }
    },
    [threadUuid, sendMessage, createNewThread]
  );

  /**
   * Add optimistic user response
   */
  const addUserResponse = useCallback((message) => {
    const optimisticInstruction = {
      type: 'UserResponse',
      content: message,
      role: 'user',
      id: `optimistic-${Date.now()}`
    };
    setInstructions((prev) => [...prev, optimisticInstruction]);
  }, []);

  /**
   * Reset conversation
   */
  const reset = useCallback(() => {
    setThreadUuid(null);
    setInstructions([]);
    setLoading(false);
    setError(null);
    isCreatingThread.current = false;
  }, []);

  return {
    // State
    threadUuid,
    instructions,
    loading,
    error,

    // Actions
    sendMessage: sendMessageOrCreateThread,
    addUserResponse,
    reset,

    // Computed
    hasThread: Boolean(threadUuid),
    hasInstructions: instructions.length > 0
  };
}
